import { computed, inject, Injectable, signal } from '@angular/core';
import { Firestore, collection, collectionData, query, where } from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { BehaviorSubject, defer, from, Observable, of } from 'rxjs';
import { concatMap, map, reduce, switchMap, tap } from 'rxjs/operators';
import { HttpClient, HttpEvent, HttpEventType, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { BoardParams, BoardParamsByUid, DashboardItem, DashboardType, RenderStrategy } from './board.types';

export const METABASE_CARD_TAG_IDS: Record<number, Record<string, string>> = {
  319: {
    sdv: '56b89435-0815-41b0-9e3c-c6a53d48fdec',
    rdv: 'abdcf0fa-dc22-43b7-bb98-8bc70b8f4241',
  },
  320: {
    sdv: '172a2074-1376-4baf-88f6-b22e2039b895',
    rdv: 'd262c8fb-d4f4-4bc9-8dd6-9a009d2e1ea9',
  },
  329: {
    sdv: '3a41aceb-ae91-4bb8-a5d5-1db9a5f52ad9',
  },
  330: {
    sdv: 'ff80ffa0-1752-4435-9cd7-61121a24e6bf',
    rdv: 'dc52346b-dc6f-4f5e-a0e8-e293186bca44',
  },
  331: {
    sdv: 'e30b2fd8-188c-49eb-a5ac-1f8e49e010ec',
    rdv: '1771bcfd-d95a-478b-9287-86ab2b83280a',
  },
  332: {
    sales_force: '2285286e-0c83-4d19-b429-904380d3f593',
    sdv_cedi: '0bd412b3-0d33-4e87-8734-515a33c505a1',
  },
  419: {
    sdv: '017585b0-abd5-4deb-8a45-de07db221088',
    vendorcode: 'fddee4f6-a966-48d7-8084-d9c58d2e2545',
    estado: 'ddabd4ec-482c-4605-ba09-e2d9916da20b',
    fecha_rango: '50742a61-e39b-4be4-8b6d-afb5c9060605',
    mision: 'cd2759d4-0659-442c-ade8-c21ce1b27b7f',
    limite: 'ddafa0f6-a9e5-444f-b720-abf8f295a2ff',
    offset: '293cbb84-be12-4a2a-84b6-e9a7c80aa3fb',
  },
  431: {
    filtro_pdv_name: '46da997a-9ca1-4ddc-87d5-eac1af5646d9',
  },
  432: {
    fecha_inicio: '7f97e2cd-c986-40e0-9ef9-e980569b9369',
    fecha_fin: '4bd4cd0a-e73c-4aa1-a7ef-47d336b6b016',
    limite: 'ce344c0c-c5c6-4a4a-aed6-f5e623468e2e',
    offset: '4723ef0b-e95d-4691-b20d-a2bdbef5f783',
  },
};

@Injectable({
    providedIn: 'root'
})
export class DashboardService {
    firestore: Firestore = inject(Firestore);
    auth = inject(Auth)
    http = inject(HttpClient);

    private selectedDashboardSubject = new BehaviorSubject<DashboardItem | null>(null);
    selectedDashboard$ = this.selectedDashboardSubject.asObservable();

    setSelectedDashboardUrl(d: DashboardItem | null) {
        this.selectedDashboardSubject.next(d);
        this.selectedDashboardUrl.set(d)
    }

    loadAndSelectDashboardById(dashboardId: string): Observable<DashboardItem | null> {
        return this.getDashboards().pipe(
            map((dashboards: DashboardItem[]) => dashboards.find(d => d.id === dashboardId) || null),
            tap(dashboard => this.setSelectedDashboardUrl(dashboard))
        );
    }


    private selectedDashboardUrl = signal<DashboardItem | null>(null);



    granular$ = computed(() => {
        const dashboard = this.selectedDashboardUrl();
        return dashboard && dashboard.queries
            ? dashboard.queries.filter((query) => query.type === 'granular')
            : [];
    });

    gestion$ = computed(() => {
        const dashboard = this.selectedDashboardUrl();
        return dashboard && dashboard.queries
            ? dashboard.queries.filter((query) => query.type === 'gestion')
            : [];
    });

    images$ = computed(() => {
        const dashboard = this.selectedDashboardUrl();
        return dashboard && dashboard.queries
            ? dashboard.queries.filter((query) => query.type === 'images')
            : [];
    });

    competencia$ = computed(() => {
        const dashboard = this.selectedDashboardUrl();
        return dashboard && dashboard.queries
            ? dashboard.queries.filter((query) => query.type === 'competencia')
            : [];
    });

    get currentBoardData(): any { return this.selectedDashboardSubject.getValue(); }

    get currentUid(): string | null { return this.auth.currentUser?.uid ?? null; }

    // Unico punto que decide que es un tablero. Devuelve null si el `type` de
    // Firestore no esta en la union, para que el llamador lo pueda reportar en
    // vez de caer en un `else` silencioso.
    resolveDashboardType(raw: unknown): DashboardType | null {
        const known: DashboardType[] = ['metabase', 'urlBi', 'params', 'code', 'dynamic'];
        return known.find((t) => t === raw) ?? null;
    }

    // Unico punto que decide como se renderiza cada tipo.
    resolveRenderStrategy(type: DashboardType | null): RenderStrategy {
        switch (type) {
            case 'metabase': return 'iframe-embed';
            case 'urlBi': return 'iframe-url';
            case 'params': return 'iframe-url-params';
            case 'code': return 'component-code';
            case 'dynamic': return 'component-dynamic';
            default: return 'desconocido';
        }
    }

    // Unico punto que resuelve los parametros efectivos: los del uid indicado y
    // solo esos. Antes cada consumidor lo hacia distinto (uno mandaba el mapa
    // completo, otro fusionaba todos los uid con Object.assign).
    getParamsForUser(parameters: BoardParamsByUid | undefined, uid: string | null): BoardParams {
        if (!uid) return {};
        return parameters?.[uid] ?? {};
    }

    // Solo estos tipos llevan los parametros a alguna consulta: `metabase` los manda
    // al embed-url firmado, `code` a las cards y `params` a los {{placeholders}}
    // de su URL. `urlBi` usa la URL tal cual y `dynamic` los ignora, asi que
    // exigirlos ahi bloquearia el acceso por un dato que nunca se enviaria.
    typeUsesParams(type: DashboardType | null): boolean {
        return type === 'metabase' || type === 'code' || type === 'params';
    }

    // `parameters_objects` declara que parametros exige el tablero. Un usuario sin
    // todos ellos no puede consultar sus queries, asi que no debe ver el tablero:
    // devuelve las claves que le faltan (vacio = puede entrar).
    getMissingParams(
        dashboard: Pick<DashboardItem, 'parameters' | 'parameters_objects'>,
        uid: string | null
    ): string[] {
        const requeridos = Object.keys(dashboard.parameters_objects ?? {});
        if (!requeridos.length) return [];
        const mios = this.getParamsForUser(dashboard.parameters, uid);
        return requeridos.filter((clave) => {
            const valor = mios[clave];
            return valor === undefined || valor === null || String(valor).trim() === '';
        });
    }

    getDashboards(): Observable<DashboardItem[]> {
        return defer(() => {
            const currentUser = this.auth.currentUser;
            if (currentUser) { return of(currentUser) } else {
                return of(null);
            }
        }).pipe(
            switchMap(user => {
                const userId = user?.uid;
                const dashboardsRef = collection(this.firestore, 'dashboards');
                const q = query(dashboardsRef, where(`roles.${userId}`, '==', 'reader'));
                return collectionData(q, { idField: 'id' }) as Observable<DashboardItem[]>;
            })
        );
    }

    downloadFile(url: string): Observable<Blob> {
        return this.http.get(url, { responseType: 'blob' });
    }

    private baseUrl = `${environment['api_backend']}/api/clients-queries`;


    downloadCsv(
        start: string,
        end: string,
        clientQueryId: number
    ): Observable<HttpEvent<Blob>> {
        const params = new HttpParams()
            .set('start_date', start)
            .set('end_date', end)
            .set('client_query_id', clientQueryId.toString());

        return this.http.get(
            `${this.baseUrl}/download-excel-opt`,
            {
                params,
                observe: 'events',
                responseType: 'blob',
                reportProgress: true
            }
        ).pipe(
            tap(event => {

                switch (event.type) {
                    case HttpEventType.Sent:
                        // console.log(' Petición enviada');
                        break;

                    case HttpEventType.ResponseHeader:
                        const contentLength = event.headers.get('Content-Length');
                        // console.log('📐 Content-Length (cabecera):', contentLength);
                        break;

                    case HttpEventType.DownloadProgress:
                        break;

                    case HttpEventType.Response:
                        // La descarga ha terminado, event.body es el Blob completo
                        console.log(' Descarga completada, Blob size =', event.body?.size, 'bytes');
                        break;
                }
            })
        ) as Observable<HttpEvent<Blob>>;
    }
    
    enbedUrl(params: any, id:number ): Observable<any> {
        const body = { dashboardId: id, params };
        return this.http.post(`${environment['api_backend']}/api/dashboards/embed-url`, body)

    }

    getMetabaseCardJson(cardId: number, parameters: unknown[] = []): Observable<unknown[]> {
        // Cuando no hay parámetros (ej. card 435 sin template-tags), omitir la clave
        // `parameters` del body — Metabase devuelve 400 si recibe un array vacío
        // sobre una card sin template-tags definidos.
        const cardTags = METABASE_CARD_TAG_IDS[cardId] ?? {};
        const sanitizedParams = parameters.map((param: any, index: number) => {
            if (param && typeof param === 'object') {
                const tag = param.target?.[1]?.[1];
                const resolvedId = (param.id && param.id !== tag)
                    ? param.id
                    : (cardTags[tag] ?? param.id ?? String(tag || `param_${index}`));
                return { ...param, id: resolvedId };
            }
            return param;
        });
        const body = sanitizedParams.length > 0 ? { parameters: sanitizedParams } : {};
        return this.http.post<unknown[]>(
            `${environment['api_backend']}/api/dashboards/metabase/card/${cardId}/query/json`,
            body,
        );
    }

}
